import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { QueueHeader } from "../../components/QueueHeader";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import { useQueueNotifications, QueueNotification } from "../../hooks/useQueueNotifications";
import { usePushSubscription } from "../../hooks/usePushSubscription";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import { secondsToHour } from "../../functions/util";
import { Clock, Users, Phone, MapPin, LogOut, User, Crown, PartyPopper, Check } from 'lucide-react';
import { useToast } from "../../hooks/useToast";

// Importar fonte Poppins
if (typeof document !== 'undefined') {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap';
  link.rel = 'stylesheet';
  if (!document.head.querySelector('link[href*="Poppins"]')) {
    document.head.appendChild(link);
  }
  
  // Configurar viewport para mobile responsivo
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.setAttribute('name', 'viewport');
    document.head.appendChild(viewport);
  }
  viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
}

type ImageType = {
  id: string;
  url: string;
};

type QueueConfigType = {
  id: string;
  able: boolean;
  limitPeople: number;
  limitTime: number;
  images: ImageType[];
  observation: string;
  useMenu: boolean;
  useLocation: boolean;
  useNinegridMenu: boolean;
  externalMenuLink: string;
  useTableManagement?: boolean;
};

type QueueType = {
  id: string;
  name: string;
  phone: string;
  people: number;
  position: number;
  seconds: number;
  minutesOnQueue: string;
  finalized: string;
  preferential?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export function QueueFind() {
  const { pluginspace_id, phone } = useParams();
  const { registerAccess, getToken, getThisPluginspace } = usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig: _getAppConfig } = useAppConfig();
  const [loading, setLoading] = useState(true);
  const [queueConfig, setQueueConfig] = useState<QueueConfigType | null>(null);
  const [queue, setQueue] = useState<QueueType | null>(null);
  const [minutesOnQueue, setMinutesOnQueue] = useState("");
  const [pluginspaceIdNumeric, setPluginspaceIdNumeric] = useState<number | null>(null);
  const showAlertEnterQueue = useRef(true);

  const toastComponent = useToast();
  const { registerPushSubscription, requestNotificationPermission } = usePushSubscription();

  // ✨ NOVO: Hook para notificações de fila via WebSocket
  const handleQueueNotification = (notification: QueueNotification) => {
    console.log('📢 Notificação de fila recebida:', notification);

    // Se foi confirmado, atualizar dados
    if (notification.status === 'confirmed') {
      // Chamar novamente getQueue para atualizar dados em tempo real
      getQueue(parseInt(queueConfig?.id || '0'), false);

      // Mostrar toast de sucesso
      toastComponent.openToast(
        'success',
        notification.message || 'Sua posição foi confirmada!',
        5000
      );
    }

    // Se foi removido, mostrar alerta
    if (notification.status === 'removed') {
      Swal.fire({
        icon: 'warning',
        title: 'Posição Removida',
        text: notification.message || 'Sua posição na fila foi removida.',
      }).then(() => navigate(`/${pluginspace_id}/queue`));
    }

    // Se foi chamado (sua vez chegou), mostrar alerta destacado
    if (notification.status === 'called') {
      Swal.fire({
        icon: 'success',
        title: 'Sua Vez Chegou! 🎉',
        text: notification.message || 'Por favor, dirija-se ao estabelecimento agora!',
        allowOutsideClick: false,
        confirmButtonColor: '#3085d6',
      });
    }

    // Se completou, mostrar mensagem
    if (notification.status === 'completed') {
      toastComponent.openToast(
        'success',
        notification.message || 'Seu atendimento foi concluído!',
        5000
      );
    }
  };

  useQueueNotifications(phone || '', pluginspaceIdNumeric?.toString() || '', handleQueueNotification);

  useEffect(() => {
    registerAccess("queue/phone");
    init();
  }, []);

  async function init() {
    setLoading(true);
    toastComponent.setToastPosition("0rem");
    // Obter o ID numérico do pluginspace
    const pluginspace = await getThisPluginspace();
    if (pluginspace && pluginspace.id) {
      setPluginspaceIdNumeric(pluginspace.id);
    }
    await getQueueConfig(true);
    setLoading(false);
  }

  const createInterval = (queueItem: QueueType) => {
    setInterval(() => {
      queueItem.seconds++;
      queueItem.minutesOnQueue = secondsToHour(queueItem.seconds);
      setMinutesOnQueue(queueItem.minutesOnQueue);
    }, 1000);
  };

  async function getQueue(queueConfigId: number, doInterval: boolean) {

    await api
      .get(`/api/queueReservation/queue/${queueConfigId}/${phone}`, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const queueResponse = response.data;
        if (queueResponse && queueResponse.data && queueResponse.data.length) {
          const queueItem = queueResponse.data[queueResponse.data.length - 1];
          if (queueItem.finalized === "removed") {
            Swal.fire({
              icon: "warning",
              title: "Atenção!",
              text: "Oi! Percebemos que sua posição na fila foi alterada e você foi retirado. Em caso de dúvidas entre em contato com a gente. ",
            }).then(() => navigate(`/${pluginspace_id}/queue`));
          }
          if (doInterval && queueItem.minutesOnQueue) {
            const seconds =
              parseInt(queueItem.minutesOnQueue.split(":")[0]) * 3600 +
              parseInt(queueItem.minutesOnQueue.split(":")[1]) * 60 +
              parseInt(queueItem.minutesOnQueue.split(":")[2]);
            queueItem.seconds = seconds;
            createInterval(queueItem);
          }

          if (showAlertEnterQueue.current) {
            toastComponent.openToast("success", `Você entrou na fila.`, 3000);
            showAlertEnterQueue.current = false;

            // 🔔 NOVO: Registrar PUSH subscription (HYBRID: PUSH + Polling)
            try {
              console.log('[QueueFind] Registrando PUSH subscription para fila:', queueItem.id);
              
              // Solicitar permissão de notificação
              await requestNotificationPermission();
              
              // Registrar subscription para receber push quando posição for confirmada
              const pushRegistered = await registerPushSubscription(
                `queue-${queueItem.id}`,
                `${window.location.href}`
              );
              
              if (pushRegistered) {
                console.log('[QueueFind] ✅ PUSH subscription registrada com sucesso');
                toastComponent.openToast(
                  "success",
                  "Notificações push ativadas - você receberá alerta quando sua posição for confirmada!",
                  4000
                );
              } else {
                console.warn('[QueueFind] ⚠️ PUSH subscription não registrada, mas polling continua funcionando');
              }
            } catch (pushError) {
              console.error('[QueueFind] Erro ao registrar PUSH:', pushError);
              // Polling funcionará como fallback, não é crítico
            }
          }

          setQueue(queueItem);
        } else {
          if (Swal.getPopup() === null) {
            Swal.fire({
              icon: "error",
              title: "Posição não encontrada",
              text: "Não encontramos sua posição na fila. Por favor, verifique seus dados e tente novamente",
            }).then(() => navigate(`/${pluginspace_id}/queue`));
          }
        }
      })
      .catch((err) => {
        if (doInterval && Swal.getPopup() === null) {
          let title = "Posição não encontrada";
          let text =
            "Não encontramos sua posição na fila. Por favor, verifique seus dados e tente novamente";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          }).then(() => navigate(`/${pluginspace_id}/queue`));
        }
      });
  }

  async function getQueueConfig(_doInterval: boolean) {
    try {
      const response = await api.get("/api/queueReservation/queueConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      });
      const queueResponse = response.data;
      if (queueResponse && queueResponse.data && Array.isArray(queueResponse.data) && queueResponse.data[0]) {
        const queueConfigItem = queueResponse.data[0];
        if (queueConfigItem && queueConfigItem.able) {
          setQueueConfig(queueConfigItem);
        } else {
          Swal.fire({
            icon: "error",
            title: "Fila não está habilitada!",
            text: "Loja não está com o esquema de filas habilitada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Fila não configurada!",
          text: "Loja não está com o esquema de filas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Fila não configurada!",
        text: "Loja não está com o esquema de filas configurada.",
      }).then(() => navigate(`/${pluginspace_id}`));
    }
  }
  async function quitQueue() {
    if (queue && queue.id) {
      setLoading(true);
      await api
        .put(
          `/api/queueReservation/queue/${queue.id}`,
          {
            finalized: "removed",
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          setLoading(false);
          const queueResponse = response.data;
          if (queueResponse.success) {
            Swal.fire({
              icon: "success",
              title: "Sucesso!",
              text: "Você saiu da fila com sucesso.",
            }).then(() => navigate(`/${pluginspace_id}/queue`));
          } else {
            Swal.fire({
              icon: "error",
              title: "Erro ao sair da fila!",
              text: "Não foi possível sair da fila.",
            }).then(async () => await init());
          }
        })
        .catch((err) => {
          setLoading(false);
          let title = "Erro ao sair da fila!";
          let text = "Não foi possível sair da fila.";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          }).then(async () => await init());
        });
    } else {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Não foi possível encontrar alguém na fila com esse número de telefone.",
      }).then(() => navigate(`/${pluginspace_id}`));
    }
  }

  function navigateToMenu() {
    try {
      if (!queueConfig) return;

      if (queueConfig.useMenu && queueConfig.useNinegridMenu) {
        window.open(`/${pluginspace_id}/products`, "_blank");
      }

      if (
        queueConfig.useMenu &&
        !queueConfig.useNinegridMenu &&
        queueConfig.externalMenuLink
      ) {
        window.open(queueConfig.externalMenuLink, "_blank");
      }
    } catch (error) {
      console.log(error);
    }
  }

  function formatTime(dateString: string): string {
    try {
      const date = new Date(dateString);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    } catch {
      return "";
    }
  }

  function calculateDeadlineTime(confirmedTime: string, limitTimeMinutes: number): string {
    try {
      const date = new Date(confirmedTime);
      date.setMinutes(date.getMinutes() + limitTimeMinutes);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    } catch {
      return "";
    }
  }

  useEffect(() => {
    if (queueConfig) {
      // Chama a função getQueue com o parâmetro true
      getQueue(parseInt(queueConfig.id), true);

      // Define um timeout para chamar a função getQueue com o parâmetro false após 2000ms
      const timeoutId = setInterval(async () => {
        if (Swal.getPopup() === null)
          await getQueue(parseInt(queueConfig.id), false);
      }, 15000);

      // Retorna uma função de limpeza para cancelar o timeout quando o componente for desmontado
      return () => clearInterval(timeoutId);
    }
  }, [queueConfig]);

  return (
    <>
    <QueueHeader />
      <div className="queue queue-find">
        {loading || !minutesOnQueue ? (
          <div style={{ width: "100%", height: "calc(100vh)" }}>
            <Loading
              type="spinningBubbles"
              color="#FFF"
              width={64}
              height={64}
            />
          </div>
        ) : (
          <div className="content-queue-find">
            
            
            {queueConfig && (
              <div className="body-queue-find">
               {/*  {queueConfig.images && queueConfig.images[1] && (
                  <div className="queue-image">
                    <img src={queueConfig.images[1].url} alt="Imagem Fila 1" />
                  </div>
                )} */}
                <div className="queue-finded">
                  <h2 className="queue-title" style={{
                    color: "#4377FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    marginTop: "20px"
                  }}>
                    {queue?.finalized === "confirmed" ? (
                      <>
                        <PartyPopper size={24} />
                        Chegou sua vez!
                      </>
                    ) : (
                      <>
                        <Check size={24} />
                        Você está na fila de espera!
                      </>
                    )}
                  </h2>
                  {queue && (
                    <div className="queue-card">
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px"
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          fontFamily: "'Poppins', sans-serif"
                        }}>
                          <div style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #4377FF 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: "24px",
                            fontWeight: "700"
                          }}>
                            {queue.name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: "18px",
                              fontWeight: "700",
                              color: "#1c1c1c",
                              marginBottom: "4px"
                            }}>
                              {queue.name}
                            </div>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "14px",
                              color: "#666"
                            }}>
                              <Phone size={14} />
                              {queue.phone}
                            </div>
                          </div>
                        </div>

                        {queue.finalized === "confirmed" && queue.updatedAt && queueConfig && (
                          <div style={{
                            background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)",
                            border: "1px solid rgba(16, 185, 129, 0.3)",
                            borderRadius: "12px",
                            padding: "16px",
                            marginTop: "12px",
                            fontFamily: "'Poppins', sans-serif"
                          }}>
                            <div style={{
                              fontSize: "16px",
                              color: "#4377FF",
                              fontWeight: "600",
                              marginBottom: "8px",
                              textAlign: "center"
                            }}>
                              Confirmação de posição
                            </div>
                            <div style={{
                              fontSize: "14px",
                              color: "#059669",
                              lineHeight: "1.6",
                              textAlign: "center"
                            }}>
                              <div>Confirmado em: <strong>{formatTime(queue.updatedAt)}</strong></div>
                              <div style={{ marginTop: "8px" }}>
                                Tolerância: <strong>{queueConfig.limitTime} minutos</strong>
                              </div>
                              <div style={{ marginTop: "8px" }}>
                                Prazo limite de chegada: <strong>{calculateDeadlineTime(queue.updatedAt, queueConfig.limitTime)}</strong>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {!queue.finalized && (
                          <div className="info-grid">
                            {/* Posição (mover para cima) */}
                            <div className="info-card">
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                marginBottom: "8px",
                                color: "#4377FF",
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: "13px",
                                fontWeight: "600"
                              }}>
                                <MapPin size={16} />
                                Posição
                              </div>
                              <div style={{
                                fontSize: "24px",
                                fontWeight: "700",
                                color: "#4377FF",
                                fontFamily: "'Poppins', sans-serif"
                              }}>
                                {typeof queue.position === "number" ? `${queue.position}ª` : queue.position}
                              </div>
                            </div>

                            {/* placeholder para forçar 'Grupo de Pessoas' para a linha abaixo da Posição */}
                            <div style={{ width: "100%", height: 0 }} />

                            {/* Grupo de Pessoas (abaixo de Posição) */}
                            
                            <div className="grid-break" />

                            <div className="info-card info-card--full">
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                marginBottom: "8px",
                                color: "#4377FF",
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: "13px",
                                fontWeight: "600"
                              }}>
                                <Users size={16} />
                                Grupos de Pessoas
                              </div>
                              <div style={{
                                fontSize: "24px",
                                fontWeight: "700",
                                color: "#4377FF",
                                fontFamily: "'Poppins', sans-serif"
                              }}>
                                {queue.people}
                              </div>
                            </div>

                            {/* Observação sutil sobre Grupo de Pessoas */}
                            <div style={{
                              gridColumn: "span 2",
                              color: "#6b7280",
                              fontSize: "13px",
                              textAlign: "center",
                              marginTop: "4px",
                              marginBottom: "4px",
                              fontFamily: "'Poppins', sans-serif",
                              opacity: 0.95
                            }}>
                              <small>
                                O grupo de pessoas indica quantas pessoas estão associadas à sua entrada (por exemplo: 2, 3, etc.).
                              </small>
                            </div>

                            {/* Tempo de espera (abaixo da observação) */}
                            {minutesOnQueue && (
                              <div className="info-card info-card--full" style={{ textAlign: "center" }}>
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  marginBottom: "8px",
                                  color: "#4377FF",
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: "13px",
                                  fontWeight: "600"
                                }}>
                                  <Clock size={16} />
                                  Tempo de espera
                                </div>
                                <div style={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  color: "#4377FF",
                                  fontFamily: "'Poppins', sans-serif"
                                }}>
                                  {minutesOnQueue}
                                </div>
                              </div>
                            )}

                            {/* Fila Preferencial */}
                            {typeof queue.preferential === 'boolean' && (
                              <div className="info-card info-card--full info-card--preferential" style={{ textAlign: "center" }}>
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  marginBottom: "8px",
                                  color: "#4377FF",
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: "13px",
                                  fontWeight: "600"
                                }}>
                                  <Crown size={16} />
                                  Fila Preferencial
                                </div>
                                <div style={{
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  color: "#4377FF",
                                  fontFamily: "'Poppins', sans-serif"
                                }}>
                                  {queue.preferential ? "Sim ✓" : "Não"}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {queue && !queue.finalized && (
                    <div
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = "0 12px 32px rgba(99, 102, 241, 0.22), 0 8px 16px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                      }}
                      style={{
                        background: "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.08) 50%, rgba(67, 56, 202, 0.1) 100%)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        borderRadius: "20px",
                        padding: "clamp(16px, 3vw, 32px)",
                        marginBottom: "clamp(20px, 3vw, 32px)",
                        boxShadow: "0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                        border: "1px solid rgba(99, 102, 241, 0.25)",
                        position: "relative" as const,
                        overflow: "hidden",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                      }}
                    >
                      <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                        pointerEvents: "none"
                      }}></div>
                      <div style={{ position: "relative", zIndex: 1 }}>
                        <div>
                          <p style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "14px",
                            color: "#666",
                            textAlign: "center",
                            marginBottom: "16px",
                            lineHeight: "1.6"
                          }}>
                            Fique atento, você será chamado em breve. Indicamos que
                            não feche esta página pois você será notificado quando
                            chegar sua vez.
                          </p>
                          {queueConfig.observation && (
                            <div
                              className="observation-queue"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: "14px",
                                color: "#666",
                                background: "#f9fafb",
                                padding: "16px",
                                borderRadius: "12px",
                                marginBottom: "24px"
                              }}
                              dangerouslySetInnerHTML={{
                                __html: queueConfig.observation,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="action-buttons">
                    {!queue?.finalized && (
                      <button
                        onClick={() =>
                          Swal.fire({
                            title: "Você tem certeza que deseja sair da fila?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Sim, quero sair",
                            cancelButtonText: "Cancelar",
                          }).then((res) => {
                            if (res.isConfirmed) {
                              quitQueue();
                            }
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "14px",
                          fontSize: "15px",
                          fontWeight: "600",
                          color: "#dc2626",
                          background: "#fff",
                          border: "2px solid #dc2626",
                          borderRadius: "12px",
                          cursor: "pointer",
                          fontFamily: "'Poppins', sans-serif",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          transition: "all 0.3s ease"
                        }}
                      >
                        <LogOut size={18} />
                        Sair da fila
                      </button>
                    )}
                    {queueConfig && queueConfig.useMenu && (
                      <button
                        onClick={navigateToMenu}
                        style={{
                          width: "100%",
                          padding: "16px",
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#fff",
                          background: "linear-gradient(135deg, #4377FF 0%, #764ba2 100%)",
                          border: "none",
                          borderRadius: "12px",
                          cursor: "pointer",
                          fontFamily: "'Poppins', sans-serif",
                          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                          transition: "all 0.3s ease",
                          marginBottom: "20px"
                        }}
                      >
                        Ver cardápio
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
